import { useEffect, useRef, useState, type ReactNode } from "react";
import { CardFrame } from "./CardFrame";
import { useAuth } from "../context/AuthContext";
import { useLbMonitor, type LbMonitorLine } from "../hooks/useLbMonitor";
import { LbSetpointTrend } from "./LbSetpointTrend";
import { canControlFcrCore } from "../lib/access";
import {
  postLbCmd,
  type LbCmd,
  type LbCmdOptions,
  type LbSetpointStatus,
  type LbStatus,
  type LbTakeover,
} from "../lib/lbApi";

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page disabled:cursor-not-allowed disabled:opacity-40 disabled:text-text-tertiary";
const btnOn = "rounded-md border border-accent bg-accent px-2 py-1 text-white";
const STALE_MS = 12_000;

function clock(ts: string) {
  if (!ts) return "--:--:--";
  const slice = ts.includes("T") ? ts.slice(11, 19) : ts.slice(0, 8);
  return slice || ts;
}

function ageMs(status: LbStatus | null, now: number) {
  if (!status?.ts) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(status.ts);
  if (Number.isNaN(parsed)) return Number.POSITIVE_INFINITY;
  return now - parsed;
}

function serviceState(status: LbStatus | null, now: number) {
  if (ageMs(status, now) > STALE_MS) return "down";
  if (status?.role === "paused") return "paused";
  return "running";
}

function stateClass(state: string) {
  if (state === "running") return "font-semibold text-status-ok";
  if (state === "down") return "font-medium text-status-error";
  return "font-medium text-text-secondary";
}

function notifyLbError(line: string) {
  if (!line.includes("LB - monitoring")) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (!document.hidden && document.hasFocus()) return;
  try {
    new Notification("Load balancing", { body: line, tag: "lb-error" });
  } catch {
    /* background tab without a notification service */
  }
}

function scUpdates(status: LbStatus) {
  if (typeof status.sc_updates === "number") return status.sc_updates;
  if (typeof status.setpointChanges === "number") return status.setpointChanges;
  return 0;
}

function Pane({
  title,
  hint,
  lines,
  action,
}: {
  title: string;
  hint: string;
  lines: LbMonitorLine[];
  action?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [frozen, setFrozen] = useState<LbMonitorLine[] | null>(null);
  const shown = frozen ?? lines;
  useEffect(() => {
    const el = box.current;
    if (el && !frozen) el.scrollTop = el.scrollHeight;
  }, [lines, frozen]);

  function onScroll() {
    const el = box.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    if (!atBottom && !frozen) setFrozen(lines);
    else if (atBottom && frozen) setFrozen(null);
  }

  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
          {title}
          <span className="ml-2 text-text-tertiary">{hint}</span>
          {frozen ? (
            <button
              type="button"
              className="ml-2 font-semibold text-text-primary underline"
              title="Scrolled up: display paused. Click or scroll to the bottom to follow again."
              onClick={() => setFrozen(null)}
            >
              paused · Follow
            </button>
          ) : null}
        </p>
        {action}
      </div>
      <div
        ref={box}
        onScroll={onScroll}
        className="h-40 overflow-auto rounded-md border border-border-subtle bg-page p-2 font-mono text-text-primary"
        style={{ fontSize: "var(--font-nano)" }}
      >
        {shown.length === 0 ? (
          <span className="text-text-tertiary">waiting…</span>
        ) : (
          shown.map((row) => (
            <div key={`${row.ts}\t${row.line}`} className="whitespace-pre">
              <span className="text-text-tertiary">{clock(row.ts)}  </span>
              {row.line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const TAKEOVER: { id: LbTakeover; label: string; title: string }[] = [
  { id: "off", label: "Off", title: "No setpoints; Blue Current backend is the sender" },
  { id: "dry", label: "Dry", title: "Schedule and log every setpoint, publish nothing" },
  { id: "on", label: "On", title: "Send SmartCurrentMax to every in-scope charger" },
  {
    id: "first50",
    label: "First 50",
    title: "Live, only in-scope chargers BCU100008 to BCU100257 (about 50)",
  },
  {
    id: "first500",
    label: "500",
    title: "Live, only in-scope chargers BCU100008 to BCU102034 (500)",
  },
];

function n(value: number | null | undefined) {
  return typeof value === "number" ? String(value) : "—";
}

function SetpointPanel({
  sp,
  host,
  disabled,
  busy,
  send,
}: {
  sp: LbSetpointStatus | undefined;
  host: string;
  disabled: boolean;
  busy: LbCmd | null;
  send: (cmd: LbCmd, opts?: LbCmdOptions) => Promise<void>;
}) {
  const takeover = sp?.takeover ?? "off";
  const r = sp?.resp1m ?? {};
  const off = disabled || busy !== null || !sp;
  const missing = [
    sp && sp.cmdClient === false ? "cmd client (LB_CMD_USER)" : null,
    sp && sp.stateClient === false ? "state client (LB_STATE_USER)" : null,
  ].filter(Boolean);

  function pickTakeover(mode: LbTakeover) {
    if (mode === takeover) return;
    if (
      mode === "on" &&
      !window.confirm(
        `Send SmartCurrentMax (${sp?.sendMode ?? "cmd_wait"}) to ${n(sp?.inScope)} chargers (${sp?.scope ?? "?"})?`,
      )
    ) {
      return;
    }
    if (
      mode === "first50" &&
      !window.confirm(
        `Send SmartCurrentMax live (${sp?.sendMode ?? "cmd_wait"}) to ${sp?.scope ?? "?"} chargers BCU100008 to BCU100257?`,
      )
    ) {
      return;
    }
    if (
      mode === "first500" &&
      !window.confirm(
        `Send SmartCurrentMax live (${sp?.sendMode ?? "cmd_wait"}) to ${sp?.scope ?? "?"} chargers BCU100008 to BCU102034 (500)?`,
      )
    ) {
      return;
    }
    void send("takeover", { mode });
  }

  return (
    <div
      className="mb-3 rounded-md border border-border-subtle p-2"
      style={{ fontSize: "var(--font-micro)" }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1">
          <span className="text-text-secondary">Setpoints</span>
          {TAKEOVER.map((t) => (
            <button
              key={t.id}
              type="button"
              className={takeover === t.id ? btnOn : btn}
              disabled={off}
              title={t.title}
              aria-pressed={takeover === t.id}
              onClick={() => pickTakeover(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-text-secondary">
          Send via
          <select
            className="rounded-md border border-border-subtle bg-page px-1 py-1 text-text-primary"
            value={sp?.sendMode ?? "cmd_wait"}
            disabled={off}
            onChange={(e) => void send("send_mode", { mode: e.target.value })}
          >
            <option value="cmd_wait">ocpp/cmd/out, track replies</option>
            <option value="cmd_nowait">ocpp/cmd/out, fire and forget</option>
            <option value="raw" disabled={!sp?.rawAvailable}>
              gw/ws/out raw{sp?.rawAvailable ? "" : " (needs gw/ws/out access)"}
            </option>
          </select>
        </label>
        <label className="flex items-center gap-1 text-text-secondary">
          Scope
          <select
            className="rounded-md border border-border-subtle bg-page px-1 py-1 text-text-primary"
            value={sp?.scope ?? "infcr_smart"}
            disabled={off}
            onChange={(e) => void send("scope", { mode: e.target.value })}
          >
            <option value="infcr_smart">inFCR + smartCharging</option>
            <option value="infcr">all inFCR</option>
          </select>
        </label>
        <label className="flex items-center gap-1 text-text-secondary">
          <input
            type="checkbox"
            checked={sp?.logSends ?? false}
            disabled={off}
            onChange={(e) => void send("log_sends", { on: e.target.checked })}
          />
          Log every send
        </label>
        <button
          type="button"
          className={btn}
          disabled={off || takeover === "off"}
          title="Queue a setpoint for every in-scope charger now (rate limited)"
          onClick={() => void send("send_now")}
        >
          Send all now
        </button>
        <button
          type="button"
          className={btn}
          disabled={off}
          title="Re-read /opt/fcr/lb/lb-allow.txt (one charger id per line; no file = no filter)"
          onClick={() => void send("reload_allow")}
        >
          Reload allow list
        </button>
      </div>
      <p className="mt-2 font-mono text-text-secondary" style={{ fontSize: "var(--font-nano)" }}>
        {sp ? (
          <>
            scope={n(sp.inScope)}
            {typeof sp.allowList === "number" ? ` (allow ${sp.allowList})` : ""}
            {"  "}state known={n(sp.stateKnown)} C={n(sp.stateC)}
            {"  "}
            <span
              className={
                (sp.needRate ?? 0) > (sp.sendRate ?? Infinity) ? "font-semibold text-status-error" : ""
              }
              title="Sends per second the timings need vs the lb.py cap (LB_SEND_RATE)"
            >
              rate need/cap={sp.needRate ?? "—"}/{n(sp.sendRate)}/s
            </span>
            {sp.boot?.active ? (
              <span className="font-semibold text-text-primary">
                {(sp.boot.cDone ?? 0) + (sp.boot.nDone ?? 0) === 0
                  ? `  bootstrap queued C ${n(sp.boot.cTotal)} non-C ${n(sp.boot.nTotal)}`
                  : `  bootstrapping C ${n(sp.boot.cDone)}/${n(sp.boot.cTotal)} non-C ${n(sp.boot.nDone)}/${n(sp.boot.nTotal)}`}
              </span>
            ) : null}
            {"  "}queue={n(sp.queued)} awaiting reply={n(sp.inflight)}
            {"  "}last 60 s: sent={n(sp.sends1m)} ok={n(r.accepted)} rej={n(r.rejected)} err={n(r.error)} timeout=
            {n(r.timeout)}
            {sp.skippedNull ? `  null=${sp.skippedNull}` : ""}
            {"  "}
            <span className={sp.staleGuard === "stale" ? "font-semibold text-status-error" : ""}>
              guard={sp.staleGuard ?? "—"}
            </span>
          </>
        ) : (
          "Waiting for setpoint block in lb/status (older lb.py?)."
        )}
      </p>
      {sp?.bad1m?.length ? (
        <p className="mt-1 font-mono text-status-error" style={{ fontSize: "var(--font-nano)" }}>
          {sp.bad1m.map((g) => (
            <span key={`${g.kind}-${g.status}`} className="mr-4">
              {g.kind} “{g.status}” ({g.cps.length}): {g.cps.join(" ")}
            </span>
          ))}
        </p>
      ) : null}
      {missing.length ? (
        <p className="mt-1 text-status-error" style={{ fontSize: "var(--font-nano)" }}>
          Missing on {host}: {missing.join(", ")}
        </p>
      ) : null}
    </div>
  );
}

export function LbView() {
  const { user } = useAuth();
  const canControl = canControlFcrCore(user);
  const [monitorOn, setMonitorOn] = useState(false);
  const [apiLines, setApiLines] = useState(false);
  const { status, stream, sent, events, connected, error } = useLbMonitor(monitorOn);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<LbCmd | null>(null);
  const [cmdError, setCmdError] = useState<string | null>(null);
  const [notifyPerm, setNotifyPerm] = useState<NotificationPermission | "unsupported">(() =>
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );
  const seenEvents = useRef(new Set<string>());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    for (const row of events) {
      const key = `${row.ts}\t${row.line}`;
      if (seenEvents.current.has(key)) continue;
      seenEvents.current.add(key);
      notifyLbError(row.line);
    }
  }, [events]);

  const state = serviceState(status, now);
  const host = status?.host || "m1-1";

  async function send(cmd: LbCmd, opts?: LbCmdOptions) {
    if (cmd === "takeover" && opts?.mode !== "off") setMonitorOn(true);
    setBusy(cmd);
    setCmdError(null);
    try {
      await postLbCmd(cmd, opts);
    } catch (err) {
      setCmdError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <CardFrame className="p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
            Load balancing
            <span className={`ml-2 ${stateClass(state)}`}>{state}</span>
            <span className="ml-2 font-normal text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
              {host} · /opt/fcr/lb
              {connected ? "" : " · stream wait"}
            </span>
          </p>
          <p className="mt-1 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            {status
              ? `v=${status.catalogVersion ?? "—"}  cps=${status.cps ?? "—"}  grids=${status.grids ?? "—"}  cp ${status.cpStatus ?? "—"}${typeof status.cpMs === "number" ? ` ${status.cpMs}ms` : ""}  gr ${status.gridStatus ?? "—"}${typeof status.gridMs === "number" ? ` ${status.gridMs}ms` : ""}  errors=${status.errors ?? 0}  sc_updates=${scUpdates(status)}`
              : "Waiting for lb/status."}
            {status?.lastError ? `  ·  ${status.lastError}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1" style={{ fontSize: "var(--font-micro)" }}>
          <button
            type="button"
            className={btn}
            disabled={!canControl || busy !== null || state === "running"}
            title={canControl ? "Resume API polling" : "Admin only"}
            onClick={() => void send("start")}
          >
            Start
          </button>
          <button
            type="button"
            className={btn}
            disabled={!canControl || busy !== null || state !== "running"}
            title={canControl ? "Stop API calls, keep the process" : "Admin only"}
            onClick={() => void send("pause")}
          >
            Pause
          </button>
          <button
            type="button"
            className={btn}
            disabled={!canControl || busy !== null || state === "down"}
            title={canControl ? "Clear the baseline and poll again" : "Admin only"}
            onClick={() => void send("reset")}
          >
            Reset
          </button>
          {notifyPerm === "default" ? (
            <button
              type="button"
              className={btn}
              onClick={() => {
                void Notification.requestPermission().then((perm) => setNotifyPerm(perm));
              }}
            >
              Notify when away
            </button>
          ) : null}
        </div>
      </div>
      {error || cmdError ? (
        <p className="mb-2 text-status-error" style={{ fontSize: "var(--font-micro)" }}>
          {cmdError || error}
        </p>
      ) : null}
      <SetpointPanel
        sp={status?.setpoint}
        host={host}
        disabled={!canControl || state === "down"}
        busy={busy}
        send={send}
      />
      <div className="grid gap-3 md:grid-cols-2">
        <Pane
          title="Stream"
          hint={monitorOn ? `lb/status · 1 Hz${apiLines ? "" : " · setpoints only"}` : "still"}
          lines={
            apiLines
              ? stream
              : stream.filter((row) => row.sp).map((row) => ({ ts: row.ts, line: `lb  ${row.sp}` }))
          }
          action={
            <div className="flex gap-1">
              <button
                type="button"
                className={apiLines ? btnOn : btn}
                style={{ fontSize: "var(--font-micro)" }}
                aria-pressed={apiLines}
                title="Show the API poll part (cp/gr status, catalog, sc_updates) in each line"
                onClick={() => setApiLines((on) => !on)}
              >
                API calls
              </button>
              <button
                type="button"
                className={monitorOn ? btnOn : btn}
                style={{ fontSize: "var(--font-micro)" }}
                aria-pressed={monitorOn}
                onClick={() => setMonitorOn((on) => !on)}
              >
                Monitor
              </button>
            </div>
          }
        />
        <Pane title="Events" hint="pause / start / reset / takeover / catalogs / errors" lines={events} />
      </div>
      <LbSetpointTrend />
      <div className="mt-3">
        <Pane
          title="Setpoints sent"
          hint={monitorOn ? "lb/setpoint/sent · every send incl. bootstrap · last 1000" : "still (Monitor)"}
          lines={sent}
        />
      </div>
    </CardFrame>
  );
}
